/**
 * Source attribution for a visitor that lands on a bare link.
 *
 * <p>Most of the links this product gets shared on - a LinkedIn post, a WhatsApp
 * message, a Discord DM - carry no query string at all. Only a deliberate
 * campaign link has UTMs, so reading UTMs alone attributes the small tagged
 * slice of traffic and silently loses the rest. `document.referrer` is the only
 * remaining evidence of where an untagged visit came from, and this module turns
 * it into the same `source` / `medium` pair a tagged link would have produced.
 * This is the fallback Google Analytics applies for untagged sessions; doing it
 * here keeps the Growth screens fed without a second analytics vendor.</p>
 *
 * <p>An explicit UTM always wins. A tagged link is a deliberate statement about
 * attribution and must never be overridden by inference, so callers pass the UTM
 * value first and only fall through to the referrer when it is absent.</p>
 *
 * <p>Values are emitted lower-cased and trimmed. The source vocabulary is a short
 * closed set rather than a raw hostname, because these strings are grouped by
 * the Growth screens: a free-form host would give every campaign its own row and
 * a page refresh with a different subdomain would split one source in two. Note
 * the set is chosen to line up with the source names the admin overview already
 * counts (`linkedin`, `google`, `community`, `referral`) so those cards start
 * reporting real numbers without a second change.</p>
 */

export interface ResolvedSource {
  /** Closed-vocabulary channel: `linkedin`, `google`, `community`, `direct`, ... */
  source: string;
  /** Standard grouping: `social`, `organic`, `referral`, `none`. */
  medium: string;
}

/** A host is reduced to something groupable: `www.` stripped, lower-cased. */
function hostOf(url: string): string {
  try {
    return new URL(url).hostname.toLowerCase().replace(/^www\./, '');
  } catch {
    return '';
  }
}

/**
 * Approximates the registrable domain so `app.nexgent.tech` and `nexgent.tech`
 * compare equal. Deliberately coarse - a full public-suffix list would be the
 * only way to do this properly, and mis-grouping a niche multi-part TLD between
 * two internal hostnames is not a failure worth the dependency.
 */
function domainOf(host: string): string {
  const parts = host.split('.').filter(Boolean);
  if (parts.length <= 2) return host;
  return parts.slice(-2).join('.');
}

function isSameSite(referrerHost: string): boolean {
  if (!referrerHost) return false;
  return domainOf(referrerHost) === domainOf(window.location.hostname);
}

/**
 * Google and Bing only count as organic when the referrer is an actual results
 * page. A plain `google.com/` referrer is a link someone clicked in a chat
 * window, which is a referral, not a search.
 */
function isSearchReferrer(referrer: string, host: string): boolean {
  if (host === 'google.com' || host.endsWith('.google.com')) {
    return /\/search|\/url\?/.test(referrer);
  }
  if (host === 'bing.com' || host.endsWith('.bing.com')) return /\/search/.test(referrer);
  if (host.endsWith('duckduckgo.com') || host === 'duckduckgo.com') return true;
  if (host === 'search.yahoo.com' || host === 'search.yahoo.co.uk') return true;
  if (host.endsWith('yandex.ru') || host.endsWith('.yandex.ru')) return true;
  return false;
}

/**
 * Host suffix -> [source, medium]. First matching suffix wins, so the more
 * specific entries are listed before the broader ones they would be caught by.
 */
const HOST_MAP: ReadonlyArray<[suffixes: string[], source: string, medium: string]> = [
  [['linkedin.com', 'lnkd.in'], 'linkedin', 'social'],
  [['l.facebook.com', 'facebook.com', 'fb.com', 'm.facebook.com'], 'facebook', 'social'],
  [['instagram.com', 'l.instagram.com'], 'instagram', 'social'],
  [['t.co', 'twitter.com', 'x.com'], 'x', 'social'],
  [['youtube.com', 'youtu.be'], 'youtube', 'social'],
  [['whatsapp.com', 'wa.me'], 'whatsapp', 'social'],
  [['telegram.org', 't.me'], 'telegram', 'social'],
  [['github.com', 'gitlab.com'], 'github', 'social'],

  // Newsletter and bulk-mail providers. Distinguished from the social rows
  // because email is one of the channels Business Support reports on separately,
  // and a newsletter click and a LinkedIn click are different things to chase.
  [
    [
      'mailchimp.com',
      'list-manage.com',
      'sendgrid.net',
      'sendinblue.com',
      'brevo.com',
      'hubspot.com',
      'hubspotemail.net',
      'campaignmonitor.com',
      'mailerlite.com',
      'convertkit.com',
      'kit.com',
      'klaviyo.com',
      'substack.com',
    ],
    'email',
    'email',
  ],

  // Founder and practitioner communities. Grouped under one source because the
  // admin overview already reports a single "community" figure and splitting it
  // into per-forum rows would bury the total.
  [['reddit.com'], 'community', 'social'],
  [['news.ycombinator.com', 'lobste.rs', 'lobsters.com'], 'community', 'social'],
  [['discord.com', 'discord.gg'], 'community', 'social'],
  [['dev.to', 'hashnode.dev'], 'community', 'social'],
  [['medium.com'], 'community', 'referral'],

  [['bing.com'], 'bing', 'organic'],
  [['duckduckgo.com'], 'duckduckgo', 'organic'],
  [['search.yahoo.com'], 'yahoo', 'organic'],
  [['yandex.ru'], 'yandex', 'organic'],
  [['google.com'], 'google', 'organic'],
];

/** Engines whose entry in HOST_MAP is organic only when reached via a results page. */
const SEARCH_ENGINES = new Set(['google', 'bing', 'duckduckgo', 'yahoo', 'yandex']);

function lookupHost(host: string): ResolvedSource | null {
  for (const [suffixes, source, medium] of HOST_MAP) {
    if (suffixes.some((suffix) => host === suffix || host.endsWith(`.${suffix}`))) {
      return { source, medium };
    }
  }
  return null;
}

/**
 * Resolves the source and medium for a landing.
 *
 * @param utmSource     the `utm_source` value, if the link carried one
 * @param referrer      `document.referrer`, normally the full referring URL
 * @returns a channel pair. Never null: an unattributable visit resolves to
 *          `direct` / `none` rather than being dropped, so the Growth screens
 *          account for every visitor instead of only the tagged ones.
 */
export function resolveSource(utmSource?: string | null, referrer?: string | null): ResolvedSource {
  const tagged = (utmSource || '').trim().toLowerCase();
  if (tagged) {
    return { source: tagged, medium: 'referral' };
  }

  if (!referrer) {
    return { source: 'direct', medium: 'none' };
  }

  const host = hostOf(referrer);
  if (!host) {
    return { source: 'direct', medium: 'none' };
  }

  if (isSameSite(host)) {
    return { source: 'internal', medium: 'referral' };
  }

  if (isSearchReferrer(referrer, host)) {
    return { source: lookupHost(host)?.source ?? 'search', medium: 'organic' };
  }

  const matched = lookupHost(host);
  if (matched) {
    // A search engine reached without a results path is a link in a chat window,
    // not a search result, so it is a referral rather than organic.
    const isEngine = SEARCH_ENGINES.has(matched.source);
    return isEngine ? { source: matched.source, medium: 'referral' } : matched;
  }

  return { source: 'referral', medium: 'referral' };
}